import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import TopBar from "@/components/TopBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Home, Plus, Copy, Users, Link as LinkIcon, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import EmptyState from "@/components/EmptyState";
import { Badge } from "@/components/ui/badge";

import { swallow } from "@/lib/log";
export default function Household() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [name, setName] = useState("My Family");
  const [active, setActive] = useState(null);
  const [shared, setShared] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/households"); setItems(data); if (data[0] && !active) setActive(data[0]); }
    catch (e) { swallow("Household")(e); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (active) loadShared(active.id); }, [active?.id]);

  const create = async () => {
    if (!name.trim()) return;
    try {
      const { data } = await api.post("/households", { name });
      setOpen(false); setName("My Family"); load(); setActive(data); toast.success("Household created");
    } catch { toast.error("Failed"); }
  };

  const invite = async () => {
    if (!active) return;
    let data;
    try {
      const res = await api.post(`/households/${active.id}/invite`, { email: inviteEmail || undefined });
      data = res.data;
    } catch { toast.error("Failed to create invite"); return; }
    try { await navigator.clipboard.writeText(data.invite_url); } catch (e) { swallow("Household/clipboard")(e); }
    toast.success(inviteEmail ? `Invite emailed to ${inviteEmail} & link copied` : "Invite link copied");
    setInviteOpen(false); setInviteEmail("");
    load();
  };

  const removeMember = async (uid) => {
    if (!active) return;
    if (!window.confirm("Remove this member?")) return;
    try { await api.delete(`/households/${active.id}/members/${uid}`); const { data } = await api.get(`/households/${active.id}`); setActive(data); }
    catch { toast.error("Failed"); }
  };

  const del = async () => {
    if (!active) return;
    if (!window.confirm(`Delete household "${active.name}"?`)) return;
    try { await api.delete(`/households/${active.id}`); setActive(null); load(); }
    catch { toast.error("Failed"); }
  };

  const loadShared = async (hid) => {
    try { const { data } = await api.get(`/households/${hid}/items`); setShared(data); }
    catch { setShared(null); }
  };

  return (
    <>
      <TopBar title="Family Vault" subtitle="Share notes and documents with loved ones" back
        right={
          <button onClick={() => setOpen(true)} className="h-9 w-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center" data-testid="household-create-button">
            <Plus className="h-4 w-4" />
          </button>
        }
      />
      <div className="px-4 py-4 space-y-4">
        {items.length === 0 && (
          <EmptyState icon={Home} title="No households yet" subtitle="Create one and invite family to share notes & documents."
            action={<Button onClick={() => setOpen(true)} data-testid="household-empty-create">Create household</Button>} />
        )}

        {items.length > 0 && (
          <div className="flex gap-2 overflow-x-auto no-scrollbar" data-testid="household-list">
            {items.map((h) => (
              <button key={h.id} onClick={() => setActive(h)} className={`shrink-0 rounded-full px-3 py-1.5 text-xs border ${active?.id === h.id ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border"}`}
                data-testid="household-chip">{h.name}</button>
            ))}
          </div>
        )}

        {active && (
          <div className="rounded-2xl border border-border bg-card p-4" data-testid="household-detail-card">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-display font-semibold">{active.name}</div>
                <div className="text-xs text-muted-foreground">{active.members.length} member{active.members.length !== 1 ? "s" : ""} • {active.is_owner ? "You are the owner" : "Member"}</div>
              </div>
              {active.is_owner && (
                <div className="flex gap-1">
                  <button onClick={() => setInviteOpen(true)} className="h-9 px-3 rounded-full bg-primary text-primary-foreground text-xs font-semibold flex items-center gap-1" data-testid="household-invite-button"><LinkIcon className="h-3 w-3" /> Invite</button>
                  <button onClick={del} className="h-9 w-9 rounded-full hover:bg-muted text-muted-foreground hover:text-destructive flex items-center justify-center" data-testid="household-delete"><Trash2 className="h-4 w-4" /></button>
                </div>
              )}
            </div>
            <div className="mt-3 space-y-1">
              {active.members.map((m) => (
                <div key={m.id} className="flex items-center justify-between rounded-xl border border-border bg-background px-3 py-2 text-xs">
                  <div className="min-w-0"><div className="font-medium truncate">{m.name || m.email}</div>
                    <div className="text-muted-foreground truncate">{m.email}</div></div>
                  {active.is_owner && m.id !== active.owner_id && (
                    <button onClick={() => removeMember(m.id)} className="h-7 w-7 rounded-full text-muted-foreground hover:text-destructive hover:bg-muted flex items-center justify-center"><Trash2 className="h-3.5 w-3.5" /></button>
                  )}
                  {m.id === active.owner_id && <Badge className="rounded-full text-[10px]">Owner</Badge>}
                </div>
              ))}
            </div>
            {active.is_owner && active.invites && active.invites.length > 0 && (
              <div className="mt-3">
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Pending invites</div>
                <div className="space-y-1">
                  {active.invites.map((inv) => (
                    <div key={inv.token} className="flex items-center gap-2 rounded-xl border border-border bg-background p-2 text-xs">
                      <div className="flex-1 min-w-0 truncate font-mono">{inv.invite_url}</div>
                      <button onClick={async () => { await navigator.clipboard.writeText(inv.invite_url); toast.success("Copied"); }} className="h-7 w-7 rounded-full hover:bg-muted flex items-center justify-center"><Copy className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {active && shared && (
          <div className="rounded-2xl border border-border bg-card p-4" data-testid="household-shared-items">
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Shared with this household</div>
            {Object.entries(shared).every(([_, v]) => (v || []).length === 0) && (
              <div className="text-sm text-muted-foreground">Nothing shared yet. From any note or document, tap Share → pick this household.</div>
            )}
            {Object.entries(shared).map(([kind, list]) => list && list.length > 0 && (
              <div key={kind} className="mt-2">
                <div className="text-xs font-medium capitalize">{kind}s ({list.length})</div>
                <div className="mt-1 space-y-1">
                  {list.slice(0, 8).map((it) => (
                    <div key={it.id} className="rounded-xl border border-border bg-background p-2 text-xs" data-testid={`shared-${kind}-item`}>
                      <div className="font-medium truncate">{it.title || it.label || it.text || it.content?.slice(0, 40) || "(untitled)"}</div>
                      <div className="text-muted-foreground truncate">by {it.owner?.name || it.owner?.email}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-display">Create household</DialogTitle></DialogHeader>
          <div>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Household name" data-testid="household-name-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} data-testid="household-save-button">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-display">Invite to household</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div className="text-xs text-muted-foreground">Optional — enter an email to send them a real invite. Either way you'll get a shareable link.</div>
            <Input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="family@example.com (optional)" data-testid="household-invite-email" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteOpen(false)}>Cancel</Button>
            <Button onClick={invite} data-testid="household-invite-send">Create invite</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
